// pages/Events.jsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  InputBase,
  Paper,
  Button,
  Grid,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert,
  Container,
  Card,
  CardContent,
  CardMedia,
  Divider,
  IconButton,
  LinearProgress
} from "@mui/material";
import { styled } from "@mui/system";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EventIcon from "@mui/icons-material/Event";
import { Link, useLocation, useNavigate } from "react-router-dom";

import LogoutNavbar from "../../components/LogoutNavbar";
import PostLoginFooter from "../../components/PostLoginFooter";
import GlobalSidebar from "../../components/GlobalSidebar";
import BackButton from "../../components/BackButton";
import api from "../../api";
import { getFullImageUrl } from "../../middleware/Imagegetter";

import {
  AccountCircle as AccountCircleIcon,
  FindInPage as LostFoundIcon,
  Lightbulb as SkillsIcon,
  Help as DoubtsIcon,
} from "@mui/icons-material";

// ---------- GREEN & BLUE COLOR THEME (Logo Match) ----------
const themeColors = {
  blue: {
    primary: "#2563EB",
    light: "#DBEAFE",
    dark: "#0d2a44",
  },
  green: {
    primary: "#16A34A",
    light: "#DCFCE7",
  },
  tech: "#0d2a44",
  cultural: "#016f57",
  sports: "#0d2a44",
  miscellaneous: "#016f57",
  totalEvents: "#2563EB",
  upcoming: "#16A34A",
  ongoing: "#2563EB",
  registered: "#16A34A",
  nextEventAccent: "#2563EB",
  nextEventBg: "#EFF6FF",
  cardHover: "#F0FDF4",
  border: "#E2E8F0",
  text: {
    primary: "#0F172A",
    secondary: "#475569",
    muted: "#64748B",
  },
  techBg: "#DBEAFE",
  culturalBg: "#DCFCE7",
  sportsBg: "#DBEAFE",
  miscellaneousBg: "#DCFCE7",
  heroGradient:
    "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)",
  buttonGradient:
    "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)",
  buttonHover:
    "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)",
  hoverLightGreen: "#F0FDF4",
  hoverLightBlue: "#EFF6FF",
};

// ---------- Styled Components (Responsive) ----------
const HeroContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  background: themeColors.heroGradient,
  borderRadius: "0 0 40px 40px",
  overflow: "hidden",
  minHeight: { xs: "40vh", sm: "45vh", md: "50vh" },
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  color: "white",
  [theme.breakpoints.down("sm")]: {
    borderRadius: "0 0 24px 24px",
    minHeight: "35vh",
  },
}));

const StatsGrid = styled(Grid)(({ theme }) => ({
  marginTop: theme.spacing(-4),
  position: "relative",
  zIndex: 10,
  padding: theme.spacing(0, 2),
  [theme.breakpoints.down("sm")]: {
    marginTop: theme.spacing(-3),
    padding: theme.spacing(0, 1),
  },
}));

const StatCard = styled(Card)(({ theme }) => ({
  background: "white",
  borderRadius: "14px",
  padding: theme.spacing(1.5),
  boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
  transition: "all 0.2s ease",
  border: `1px solid ${themeColors.border}`,
  position: "relative",
  overflow: "hidden",
  height: "100%",
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: "0 12px 26px rgba(0,0,0,0.10)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
    borderRadius: "12px",
  },
}));

const NextEventCard = styled(Card)(({ theme, bgimage }) => ({
  borderRadius: "20px",
  boxShadow: "0 8px 20px rgba(0,0,0,0.04)",
  border: "1px solid #F1F5F9",
  marginBottom: theme.spacing(4),
  position: "relative",
  overflow: "hidden",
  minHeight: { xs: "200px", sm: "180px", md: "160px" },
  backgroundImage: `url(${bgimage})`,
  backgroundSize: "cover",
  color: "white",
  backgroundPosition: "center",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
    background: "linear-gradient(90deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.45) 55%, rgba(0,0,0,0.20) 100%)",
  },
  [theme.breakpoints.down("sm")]: {
    borderRadius: "16px",
    marginBottom: theme.spacing(3),
    minHeight: "180px",
    "&::before": {
      background: "linear-gradient(180deg, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.35) 100%)",
    },
  },
}));

const NextEventContent = styled(CardContent)(({ theme }) => ({
  position: "relative",
  zIndex: 2,
  padding: theme.spacing(2, 3),
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    alignItems: "flex-start",
    padding: theme.spacing(2),
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
  },
}));

const CategoryHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(4),
  [theme.breakpoints.down("sm")]: {
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(3),
  },
}));

const EventCardNew = styled(Card)(({ theme }) => ({
  borderRadius: "16px",
  overflow: "hidden",
  boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
  transition: "all 0.2s ease",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  border: "1px solid #F1F5F9",

  // ✅ FIXED WIDTH (removed vw)
  minWidth: { xs: 280, sm: 280, md: 320 },
  maxWidth: { xs: 320, sm: 300, md: 320 },

  [theme.breakpoints.down("sm")]: {
    minWidth: 280,
    maxWidth: 320,
    borderRadius: "14px",
  },

  "&:hover": {
    transform: { xs: "none", md: "translateY(-4px)" },
    boxShadow: {
      xs: "0 4px 12px rgba(0,0,0,0.04)",
      md: "0 16px 32px rgba(37, 99, 235, 0.12)"
    },
    borderColor: { md: themeColors.blue.light },
  },
}));

const SearchBarPaper = styled(Paper)(({ theme }) => ({
  background: "white",
  borderRadius: "50px",
  padding: "4px 4px 4px 16px",
  width: "100%",
  maxWidth: { xs: "100%", sm: 500, md: 600 },
  display: "flex",
  alignItems: "center",
  margin: "24px auto 0",
  border: "1px solid #E2E8F0",
  boxShadow: "0 8px 20px rgba(0,0,0,0.04)",
  [theme.breakpoints.down("sm")]: {
    padding: "4px 4px 4px 12px",
    borderRadius: "30px",
    margin: "16px auto 0",
    flexWrap: "wrap",
    background: "white",
  },
}));

// ---------- Component ----------
export default function Events({ embedded = false } = {}) {
  const footerRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();

  const computeEventStats = (list) => {
    const eventsList = Array.isArray(list) ? list : [];
    const nowMs = Date.now();
    const now = new Date(nowMs);
    const todayStart = new Date(now);
    todayStart.setHours(0, 0, 0, 0);
    const todayStartMs = todayStart.getTime();
    const tomorrowStartMs = todayStartMs + 24 * 60 * 60 * 1000;

    const normalizeIsoDay = (raw) => {
      if (!raw) return null;
      const rawStr = String(raw);
      const datePart = rawStr.includes('T') ? rawStr.split('T')[0] : rawStr;
      if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return datePart;
      const dmy = datePart.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
      if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
      const d = new Date(rawStr);
      if (Number.isNaN(d.getTime())) return null;
      const yyyy = String(d.getFullYear());
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };

    const getDayStartMs = (event) => {
      const isoDay = normalizeIsoDay(event?.date);
      if (!isoDay) return Number.NaN;
      const d = new Date(`${isoDay}T00:00:00`);
      return Number.isNaN(d.getTime()) ? Number.NaN : d.getTime();
    };

    const getStartMsForStats = (event) => {
      const isoDay = normalizeIsoDay(event?.date);
      if (!isoDay) return Number.NaN;
      const rawTime = typeof event?.time === 'string' ? event.time.trim() : '';
      const m = rawTime.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
      if (!m) {
        const d = new Date(`${isoDay}T00:00:00`);
        return Number.isNaN(d.getTime()) ? Number.NaN : d.getTime();
      }
      const hh = String(Math.max(0, Math.min(23, Number(m[1])))).padStart(2, '0');
      const min = String(Math.max(0, Math.min(59, Number(m[2])))).padStart(2, '0');
      const d = new Date(`${isoDay}T${hh}:${min}:00`);
      return Number.isNaN(d.getTime()) ? Number.NaN : d.getTime();
    };

    let total = eventsList.length;
    let upcoming = 0;
    let ongoing = 0;
    let registered = 0;

    for (const e of eventsList) {
      const status = String(e?.status || '').toLowerCase();
      const isOpen = status === 'open' || status === '';
      const dayStartMs = getDayStartMs(e);
      const startMs = getStartMsForStats(e);

      // Some backends provide an explicit per-user registration flag; if it exists, trust it.
      // Otherwise, fall back to counting events that are "registerable" (open + has a link).
      const hasRegistrationInfo =
        e != null &&
        (Object.prototype.hasOwnProperty.call(e, 'is_registered') ||
          Object.prototype.hasOwnProperty.call(e, 'isRegistered') ||
          Object.prototype.hasOwnProperty.call(e, 'registered') ||
          Object.prototype.hasOwnProperty.call(e, 'user_registered') ||
          Object.prototype.hasOwnProperty.call(e, 'has_registered'));
      const isRegistered = Boolean(
        e?.is_registered ||
          e?.isRegistered ||
          e?.registered ||
          e?.user_registered ||
          e?.has_registered
      );
      const hasLink = typeof e?.link === 'string' && e.link.trim().length > 0;
      const isRegisterable = isOpen && hasLink;
      if (hasRegistrationInfo ? isRegistered : isRegisterable) registered += 1;

      if (!isOpen) continue;
      if (!Number.isFinite(dayStartMs) || !Number.isFinite(startMs)) continue;

      const dayEndMs = dayStartMs + 24 * 60 * 60 * 1000 - 1;

      if (startMs >= tomorrowStartMs) {
        upcoming += 1;
        continue;
      }

      if (startMs >= todayStartMs && startMs < tomorrowStartMs) {
        if (startMs > nowMs) upcoming += 1;
        else if (nowMs <= dayEndMs) ongoing += 1;
        continue;
      }
    }

    total = Number.isFinite(total) ? total : 0;
    upcoming = Number.isFinite(upcoming) ? upcoming : 0;
    ongoing = Number.isFinite(ongoing) ? ongoing : 0;
    registered = Number.isFinite(registered) ? registered : 0;

    return { total, upcoming, ongoing, registered };
  };

  const getEventDetailsPath = (eventId) => {
    const path = String(location?.pathname || '').toLowerCase();
    if (path.startsWith('/clubmember/')) return `/clubmember/event/${eventId}`;
    if (path.startsWith('/student/')) return `/student/event/${eventId}`;
    return `/event/${eventId}`;
  };

  const normalizeArray = (value) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.results)) return value.results;
    if (Array.isArray(value?.data)) return value.data;
    return [];
  };

  const getEventSummaryText = (event) => {
    const ai = event?.ai_summary;
    if (typeof ai === 'string') return ai;
    if (ai == null) {
      const details = event?.details || "";
      return details ? `${details.substring(0, 80)}...` : "3 Line description...";
    }
    return "3 Line description...";
  };

  const getEventCategoryKey = (event) => {
    // Backend now provides a clear type: tech | cultural | sports (and sometimes legacy values).
    const rawType = String(event?.type ?? event?.category ?? event?.event_type ?? "")
      .toLowerCase()
      .trim();

    if (rawType === "tech" || rawType === "cultural" || rawType === "sports") return rawType;

    // Legacy / unexpected values (e.g., "group", "solo", older strings)
    // Keep a predictable bucket so filtering doesn't hide events.
    return "tech";
  };

  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [meRole, setMeRole] = useState('');

  const profilePath = (() => {
    if (meRole === 'club_member') return '/login/clubmember/home';
    if (meRole === 'admin' || meRole === 'college_admin') return '/login/admin/home';
    return '/login/student/home';
  })();

  const sidebarItems = [
    { 
      label: 'Profile', 
      path: profilePath, 
      action: () => navigate(profilePath),
      icon: <AccountCircleIcon />
    },
    { 
      label: 'Lost and Found', 
      path: '/lostfound', 
      action: () => navigate('/lostfound'),
      icon: <LostFoundIcon />
    },
    { 
      label: 'Skills', 
      path: '/skills', 
      action: () => navigate('/skills'),
      icon: <SkillsIcon />
    },
    { 
      label: 'Doubts', 
      path: '/doubts', 
      action: () => navigate('/doubts'),
      icon: <DoubtsIcon />
    },
    { 
      label: 'Events', 
      path: '/events', 
      action: () => navigate('/events'),
      icon: <EventIcon />,
      active: true
    },
  ];

  useEffect(() => {
    if (!embedded) {
      api
        .get('users/me/')
        .then((me) => setMeRole((me?.role || '').toString()))
        .catch(() => {});
    }

    const fetchevents = async () => {
      setLoading(true);
      try {
        const response = await api.get("events/event/");
        const eventsList = normalizeArray(response);
        setEvents(eventsList);
      } catch (error) {
        // console.log("Error fetching events:", error);
        const msg =
          error?.error ||
          error?.detail ||
          error?.message ||
          "Failed to load events";
        setEvents([]);
        setSnackbarMessage(msg);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };
    fetchevents();
  }, [embedded]);

  const filteredEvents = useMemo(() => {
    const q = (search || "").toLowerCase().trim();

    return (events || []).filter((event) => {
      const matchesSearch =
        q.length === 0 ||
        String(event?.title || "").toLowerCase().includes(q) ||
        String(event?.details || "").toLowerCase().includes(q) ||
        String(event?.club || "").toLowerCase().includes(q) ||
        String(event?.club_name || "").toLowerCase().includes(q) ||
        String(event?.location || "").toLowerCase().includes(q);

      const category = getEventCategoryKey(event);
      const matchesFilter = filter === "all" || category === filter;
      return matchesSearch && matchesFilter;
    });
  }, [events, search, filter]);

  function formatDateDDMMYYYY(value) {
    if (!value) return "";
    const raw = String(value);
    const datePart = raw.includes("T") ? raw.split("T")[0] : raw;

    // If already DD-MM-YYYY, keep it.
    if (/^\d{2}-\d{2}-\d{4}$/.test(datePart)) return datePart;

    // Handle YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      const [yyyy, mm, dd] = datePart.split("-");
      return `${dd}-${mm}-${yyyy}`;
    }

    // Fallback: try Date parsing.
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return raw;
    const dd = String(parsed.getDate()).padStart(2, "0");
    const mm = String(parsed.getMonth() + 1).padStart(2, "0");
    const yyyy = String(parsed.getFullYear());
    return `${dd}-${mm}-${yyyy}`;
  }

  function getEventStartMs(event) {
    const rawDate = event?.date;
    if (!rawDate) return Number.POSITIVE_INFINITY;

    // If backend already sends a full datetime, prefer it.
    const rawDateStr = String(rawDate);
    if (rawDateStr.includes("T")) {
      const d = new Date(rawDateStr);
      return Number.isNaN(d.getTime()) ? Number.POSITIVE_INFINITY : d.getTime();
    }

    // Support DD-MM-YYYY / DD/MM/YYYY (common non-ISO backend formats)
    // Convert to YYYY-MM-DD to keep parsing consistent.
    const dmyMatch = rawDateStr.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
    const normalizedDateStr = dmyMatch
      ? `${dmyMatch[3]}-${dmyMatch[2]}-${dmyMatch[1]}`
      : rawDateStr;

    // If date is YYYY-MM-DD, combine with time when present.
    const dateOnlyMatch = normalizedDateStr.match(/^(\d{4}-\d{2}-\d{2})$/);
    if (dateOnlyMatch) {
      const day = dateOnlyMatch[1];
      const rawTime = typeof event?.time === "string" ? event.time.trim() : "";
      const timeMatch = rawTime.match(/^(\d{1,2}:\d{2})(?::\d{2})?$/);

      // If no time provided, treat as end-of-day so "today" still counts upcoming.
      const iso = timeMatch ? `${day}T${timeMatch[1]}:00` : `${day}T23:59:59`;
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? Number.POSITIVE_INFINITY : d.getTime();
    }

    const d = new Date(normalizedDateStr);
    return Number.isNaN(d.getTime()) ? Number.POSITIVE_INFINITY : d.getTime();
  }

  const getEventBackgroundImage = (event) => {
    if (event?.image) {
      return getFullImageUrl(
        typeof event.image === "string"
          ? event.image
          : event.image?.url || event.image?.secure_url || ""
      );
    }
    if (event?.title?.toLowerCase().includes('rose') || event?.title?.toLowerCase().includes('love')) {
      return "https://images.unsplash.com/photo-1547045662-e5a75ee72336?w=1200";
    }
    if (event?.type?.toLowerCase() === 'miscellaneous') {
      return "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=1200";
    }
    return "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1200";
  };

  const formatPrettyDate = (value) => {
    return formatDateDDMMYYYY(value);
  };

  const formatPrettyTime12h = (value) => {
    if (!value) return "";
    if (value instanceof Date) {
      return value.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", hour12: true });
    }
    if (typeof value !== "string") return "";
    if (value.includes("T") || /\d{4}-\d{2}-\d{2}/.test(value)) {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", hour12: true });
      }
    }
    const m = value.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (m) {
      const h24 = Number(m[1]);
      const min = Number(m[2]);
      if (Number.isFinite(h24) && Number.isFinite(min) && h24 >= 0 && h24 <= 23 && min >= 0 && min <= 59) {
        const suffix = h24 >= 12 ? "PM" : "AM";
        let h12 = h24 % 12;
        if (h12 === 0) h12 = 12;
        return `${h12}:${String(min).padStart(2, "0")} ${suffix}`;
      }
    }
    return value;
  };

  const getNextEventDateTimeText = (event) => {
    if (!event) return "";
    const rawDate = event?.date;
    const rawTime = event?.time;
    const dateLabel = formatPrettyDate(rawDate);
    let timeLabel = formatPrettyTime12h(rawTime);
    if (!timeLabel && typeof rawDate === "string" && (rawDate.includes("T") || rawDate.includes(":"))) {
      timeLabel = formatPrettyTime12h(rawDate);
    }
    if (dateLabel && timeLabel) return `${dateLabel} | ${timeLabel}`;
    return dateLabel || timeLabel || "";
  };

  const nextEvent = useMemo(() => {
    const nowMs = Date.now();
    const byDateAsc = (a, b) => getEventStartMs(a) - getEventStartMs(b);

    const upcomingOpen = (events || [])
      .filter((e) => e?.status === "open")
      .filter((e) => getEventStartMs(e) >= nowMs)
      .sort(byDateAsc);

    if (upcomingOpen.length > 0) return upcomingOpen[0];

    const upcomingAny = (events || [])
      .filter((e) => getEventStartMs(e) >= nowMs)
      .sort(byDateAsc);

    return upcomingAny[0] || null;
  }, [events]);

  const sortedFilteredEvents = useMemo(() => {
    const nowMs = Date.now();
    const withMs = (filteredEvents || []).map((e) => ({ e, ms: getEventStartMs(e) }));
    const sortAsc = (a, b) => a.ms - b.ms;

    const upcoming = withMs.filter((x) => x.ms >= nowMs).sort(sortAsc);
    const past = withMs.filter((x) => x.ms < nowMs).sort(sortAsc);

    return [...upcoming, ...past].map((x) => x.e);
  }, [filteredEvents]);

  // The list we render (and that stats should reflect).
  const visibleEvents = sortedFilteredEvents;
  const stats = useMemo(() => computeEventStats(visibleEvents), [visibleEvents]);

  return (
    <Box sx={{ 
      display: "flex", 
      flexDirection: "column", 
      minHeight: embedded ? "auto" : "100vh", 
      background: "#FFFFFF",
      width: "100%",
      overflowX: "hidden"
    }}>
      {!embedded && <LogoutNavbar sidebarItems={isMobile ? sidebarItems : []} />}
      <Box sx={{ 
        display: "flex", 
        flexGrow: 1,
        flexDirection: { xs: "column", md: "row" }
      }}>
        {!embedded && !isMobile && <GlobalSidebar />}
        
        <Box 
          component="main" 
          sx={{ 
            flexGrow: 1, 
            overflowX: "hidden",
            width: { xs: "100%", md: "calc(100% - 280px)" }
          }}
        >
          {/* Hero Section */}
          <HeroContainer>
            <Container 
              maxWidth="lg" 
              sx={{ 
                px: { xs: 2, sm: 3, md: 4 },
                py: { xs: 4, sm: 6, md: 8 }
              }}
            >
              {!embedded && <BackButton tone="light" sx={{ mb: 2, mr: 210}} />}
              <Box sx={{ position: "relative", zIndex: 2 }}>
                <Typography
                  variant="h1"
                  sx={{
                    fontFamily: '"Great Vibes", cursive',
                    fontSize: { 
                      xs: "3.5rem", 
                      sm: "4.5rem", 
                      md: "6rem", 
                      lg: "8.2rem" 
                    },
                    fontWeight: 400,
                    mb: { xs: 1, sm: 2 },
                    letterSpacing: "-0.02em",
                    textShadow: "0 4px 20px rgba(0,0,0,0.1)",
                    lineHeight: { xs: 1.2, sm: 1.1, md: 1 },
                  }}
                >
                  Campus Events
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    mb: { xs: 2, sm: 3, md: 4 },
                    opacity: 0.95,
                    fontWeight: 400,
                    maxWidth: "700px",
                    margin: "0 auto",
                    fontSize: { 
                      xs: "0.875rem", 
                      sm: "1rem", 
                      md: "1.2rem" 
                    },
                    px: { xs: 2, sm: 0 },
                  }}
                >
                  Discover exciting events, sports, and activities happening across campus.
                  Connect, learn, and grow with your peers.
                </Typography>
                
                {/* Search Bar */}
                <SearchBarPaper>
                  <SearchIcon sx={{ color: "#94A3B8", mr: 1, fontSize: { xs: 18, sm: 20 } }} />
                  <InputBase
                    placeholder={isSmallMobile ? "Search events..." : "Search events, clubs, or locations..."}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{
                      flex: 1,
                      fontSize: { xs: "0.875rem", sm: "0.95rem" },
                      color: "#0F172A",
                      "&::placeholder": {
                        color: "#94A3B8",
                        fontSize: { xs: "0.875rem", sm: "0.95rem" },
                      },
                    }}
                  />
                  {search?.length > 0 && (
                    <IconButton
                      onClick={() => setSearch("")}
                      size="small"
                      sx={{
                        mr: 0.5,
                        color: "#94A3B8",
                        "&:hover": { background: "transparent", color: themeColors.text.primary },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                    </IconButton>
                  )}
                  <Button
                    variant="contained"
                    sx={{
                      background: themeColors.buttonGradient,
                      borderRadius: "50px",
                      px: { xs: 4, sm: 3, md: 4 },
                      py: { xs: 1, sm: 1.5 },
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: { xs: "0.875rem", sm: "0.95rem" },
                      minWidth: { xs: "70px", sm: "auto" },
                      "&:hover": {
                        background: themeColors.buttonHover,
                      },
                    }}
                  >
                    {isSmallMobile ? "Go" : "Search"}
                  </Button>
                </SearchBarPaper>

                {/* Filter row */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: { xs: 0.8, sm: 1.5 },
                    mt: { xs: 2, sm: 2.5 },
                    flexWrap: "wrap",
                    px: { xs: 1, sm: 2 },
                  }}
                >
                  {[
                    { key: "all", label: "All" },
                    { key: "tech", label: "Tech" },
                    { key: "cultural", label: "Cultural" },
                    { key: "sports", label: "Sports" },
                  ].map((item) => {
                    const active = filter === item.key;
                    return (
                      <Button
                        key={item.key}
                        onClick={() => setFilter(item.key)}
                        sx={{
                            flex: 1,                         
                            height: { xs: 32, sm: 36 },
                            borderRadius: "10px",
                            fontWeight: 700,
                            textTransform: "none",
                            fontSize: { xs: "0.75rem", sm: "0.85rem" },

                            border: active ? "none" : `1px solid rgba(255,255,255,0.55)`,
                            backgroundColor: active
                              ? "rgba(0,0,0,0.35)"
                              : "rgba(255,255,255,0.12)",
                            color: "white",

                            backdropFilter: "blur(6px)",

                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",

                            minWidth: 0,                     
                            whiteSpace: "nowrap",

                            "&:hover": {
                              backgroundColor: active
                                ? "rgba(0,0,0,0.40)"
                                : "rgba(255,255,255,0.18)",
                            },
                          }}

                      >
                        {item.label}
                      </Button>
                    );
                  })}
                </Box>
              </Box>
            </Container>
          </HeroContainer>

          {/* Stats Section */}
          <Container 
            maxWidth="lg" 
            sx={{ 
              px: { xs: 10, sm: 15, md: 25 },
              mt: { xs: 2, sm: 3, md: 4 }
            }}
          >
            <StatsGrid container spacing={{ xs: 1, sm: 20, md: 3 }}>
              <Grid item xs={6} sm={6} md={3}>
                <StatCard>
                  <Box sx={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 34, background: themeColors.green.light, opacity: 0.35 }} />
                  <Box sx={{ 
                    position: "relative", 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "space-between", 
                    gap: 0.5,
                    flexDirection: { xs: "column", sm: "row" }
                  }}>
                    <Typography
                      variant="h3"
                      fontWeight={1000}
                      sx={{
                        color: themeColors.green.primary,
                        fontSize: { xs: "1.75rem", sm: "2rem", md: "2.6rem" },
                        lineHeight: 1,
                      }}
                    >
                      {stats.total}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: themeColors.text.primary,
                        fontWeight: 900,
                        lineHeight: 1.1,
                        textAlign: { xs: "center", sm: "right" },
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      }}
                    >
                      Total
                      <br />
                      Events
                    </Typography>
                  </Box>
                </StatCard>
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <StatCard>
                  <Box sx={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 34, background: themeColors.blue.light, opacity: 0.35 }} />
                  <Box sx={{ 
                    position: "relative", 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "space-between", 
                    gap: 0.5,
                    flexDirection: { xs: "column", sm: "row" }
                  }}>
                    <Typography
                      variant="h3"
                      fontWeight={900}
                      sx={{
                        color: themeColors.green.primary,
                        fontSize: { xs: "1.75rem", sm: "2rem", md: "2.6rem" },
                        lineHeight: 1,
                      }}
                    >
                      {stats.upcoming}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: themeColors.text.primary,
                        fontWeight: 800,
                        lineHeight: 1.1,
                        textAlign: { xs: "center", sm: "right" },
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      }}
                    >
                      Upcoming
                      <br />
                      Events
                    </Typography>
                  </Box>
                </StatCard>
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <StatCard>
                  <Box sx={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 34, background: themeColors.green.light, opacity: 0.35 }} />
                  <Box sx={{ 
                    position: "relative", 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "space-between", 
                    gap: 0.5,
                    flexDirection: { xs: "column", sm: "row" }
                  }}>
                    <Typography
                      variant="h3"
                      fontWeight={900}
                      sx={{
                        color: themeColors.green.primary,
                        fontSize: { xs: "1.75rem", sm: "2rem", md: "2.6rem" },
                        lineHeight: 1,
                      }}
                    >
                      {stats.ongoing}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: themeColors.text.primary,
                        fontWeight: 800,
                        lineHeight: 1.1,
                        textAlign: { xs: "center", sm: "right" },
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      }}
                    >
                      Ongoing
                      <br />
                      Events
                    </Typography>
                  </Box>
                </StatCard>
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <StatCard>
                  <Box sx={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 34, background: themeColors.blue.light, opacity: 0.35 }} />
                  <Box sx={{ 
                    position: "relative", 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "space-between", 
                    gap: 0.5,
                    flexDirection: { xs: "column", sm: "row" }
                  }}>
                    <Typography
                      variant="h3"
                      fontWeight={900}
                      sx={{
                        color: themeColors.green.primary,
                        fontSize: { xs: "1.75rem", sm: "2rem", md: "2.6rem" },
                        lineHeight: 1,
                      }}
                    >
                      {stats.registered}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: themeColors.text.primary,
                        fontWeight: 800,
                        lineHeight: 1.1,
                        textAlign: { xs: "center", sm: "right" },
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      }}
                    >
                      Registered
                      <br />
                      Events
                    </Typography>
                  </Box>
                </StatCard>
              </Grid>
            </StatsGrid>
          </Container>

          <Container 
            maxWidth="lg" 
            sx={{ 
              mt: { xs: 2, sm: 3, md: 4 }, 
              pb: { xs: 4, sm: 6, md: 8 },
              px: { xs: 2, sm: 3, md: 4 }
            }}
          >
            {/* Next Event Section */}
            {nextEvent && (
              <NextEventCard bgimage={getEventBackgroundImage(nextEvent)}>
                <NextEventContent>
                  <Box sx={{ flex: 1, minWidth: 0, width: { xs: "100%", md: "auto" } }}>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        backgroundColor: themeColors.green.primary,
                        color: "white",
                        fontWeight: 700,
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                        px: { xs: 1, sm: 1.5 },
                        py: { xs: 0.4, sm: 0.6 },
                        borderRadius: "10px",
                        mb: { xs: 0.8, sm: 1.2 },
                        lineHeight: 1,
                      }}
                    >
                      Next Event
                    </Box>

                    <Typography
                      variant="h3"
                      fontWeight={800}
                      sx={{
                        color: "white",
                        fontSize: { xs: "1.5rem", sm: "2rem", md: "2.75rem" },
                        lineHeight: 1.05,
                        mb: 0.5,
                        textShadow: "0 2px 8px rgba(0,0,0,0.35)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: { xs: "normal", md: "nowrap" },
                        wordBreak: "break-word",
                      }}
                    >
                      {nextEvent.title || "Rose Day"}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(255,255,255,0.92)",
                        fontWeight: 500,
                        mb: { xs: 0.8, sm: 1.4 },
                        textShadow: "0 1px 4px rgba(0,0,0,0.25)",
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      }}
                    >
                      {getNextEventDateTimeText(nextEvent) || "Feb 10, 2026 | 7:00 PM"}
                    </Typography>

                    <Box sx={{ 
                      display: "flex", 
                      alignItems: "flex-start", 
                      gap: { xs: 0.8, sm: 1.2 }, 
                      mb: { xs: 0.8, sm: 1.2 } 
                    }}>
                      <LocationOnIcon sx={{ 
                        fontSize: { xs: 16, sm: 18 }, 
                        color: "rgba(255,255,255,0.9)", 
                        mt: "2px" 
                      }} />
                      <Typography
                        variant="body2"
                        sx={{
                          color: "rgba(255,255,255,0.92)",
                          fontWeight: 500,
                          textShadow: "0 1px 4px rgba(0,0,0,0.25)",
                          lineHeight: 1.25,
                          wordBreak: "break-word",
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        }}
                      >
                        {(nextEvent.location || "Second Campus Ground").split(",").map((part, idx) => (
                          <span key={idx} style={{ display: "block" }}>
                            {part.trim()}
                          </span>
                        ))}
                      </Typography>
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(255,255,255,0.9)",
                        textShadow: "0 1px 4px rgba(0,0,0,0.25)",
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      }}
                    >
                      Organised by : <strong style={{ fontWeight: 800 }}>{nextEvent.club || "EnLit"}</strong>
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      width: { xs: "100%", md: "auto" },
                      mt: { xs: 1.5, md: 0 },
                      justifyContent: { xs: "flex-start", md: "flex-end" },
                      position: { xs: "static", md: "absolute" },
                      right: { md: 18 },
                      bottom: { md: 18 },
                      zIndex: 3,
                    }}
                  >
                    <Button
                      component={Link}
                      to={getEventDetailsPath(nextEvent.id)}
                      sx={{
                        backgroundColor: themeColors.green.dark,
                        color: "white",
                        borderRadius: "10px",
                        px: { xs: 2, sm: 2.2 },
                        py: { xs: 0.7, sm: 0.9 },
                        fontWeight: 700,
                        textTransform: "none",
                        fontSize: { xs: "0.8rem", sm: "0.875rem" },
                        minWidth: { xs: 110, sm: 130 },
                        width: { xs: "100%", sm: "auto" },
                        boxShadow: "0 8px 18px rgba(0,0,0,0.22)",
                        "&:hover": {
                          backgroundColor: themeColors.buttonHover,
                          boxShadow: "0 10px 22px rgba(0,0,0,0.28)",
                        },
                      }}
                    >
                      View Event
                    </Button>
                  </Box>
                </NextEventContent>
              </NextEventCard>
            )}

            {/* LATEST EVENTS Header */}
            <CategoryHeader>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 900,
                  color: themeColors.green.dark,
                  letterSpacing: { xs: "0.04em", sm: "0.06em" },
                  fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
                }}
              >
                LATEST EVENTS
              </Typography>
              {!isSmallMobile && visibleEvents.length > 0 && (
                <Typography
                  variant="body2"
                  sx={{
                    color: themeColors.text.muted,
                    fontWeight: 600,
                  }}
                >
                  {visibleEvents.length} events found
                </Typography>
              )}
            </CategoryHeader>

            {/* Loading State */}
            {loading && (
              <Box sx={{ width: "100%", mt: { xs: 2, sm: 4 } }}>
                <LinearProgress sx={{ 
                  height: { xs: 3, sm: 4 }, 
                  borderRadius: 2,
                  backgroundColor: "#F1F5F9",
                  "& .MuiLinearProgress-bar": {
                    background: themeColors.buttonGradient,
                  }
                }} />
              </Box>
            )}

            {/* Latest events cards */}
            {!loading && visibleEvents.length > 0 && (
              <Box
                sx={{
                  backgroundColor: "white",
                  borderRadius: { xs: "16px", sm: "20px" },
                  p: { xs: 1.5, sm: 2, md: 3 },
                  border: `1px solid ${themeColors.border}`,
                }}
              >
                <Box
                    sx={{
                      display: { xs: "flex", md: "grid" },

                      // ✅ MOBILE SWIPE
                      overflowX: { xs: "auto", md: "visible" },
                      scrollSnapType: { xs: "x mandatory", md: "none" },
                      WebkitOverflowScrolling: "touch",

                      // ✅ DESKTOP GRID
                      gridTemplateColumns: { md: "repeat(3, 1fr)" },

                      gap: { xs: 2, sm: 3 },
                      alignItems: "stretch",

                      "&::-webkit-scrollbar": {
                        height: 6,
                      },
                      "&::-webkit-scrollbar-thumb": {
                        background: "#CBD5E1",
                        borderRadius: "10px",
                      },
                    }}
                  >

                  {visibleEvents.map((event) => {
                    const locationShort = (event?.location || "Second Campus Ground").split(",")[0].trim();

                    const categoryKey = getEventCategoryKey(event);
                    const tag = categoryKey === "cultural" ? "Cultural" : categoryKey === "sports" ? "Sports" : "Tech";
                    const tagBg =
                      categoryKey === "cultural"
                        ? themeColors.culturalBg
                        : categoryKey === "sports"
                          ? themeColors.sportsBg
                          : themeColors.techBg;

                    return (
                      <EventCardNew
                        key={event.id}
                        component={Link}
                        to={getEventDetailsPath(event.id)}
                        sx={{
                          flex: { xs: "0 0 85%", sm: "0 0 70%", md: "unset" },
                          scrollSnapAlign: { xs: "center", md: "unset" },
                          minWidth: { xs: "85%", sm: "70%", md: "auto" },
                          maxWidth: { xs: "100%", sm: "100%", md: "auto" },
                          textDecoration: "none",
                          color: themeColors.green.dark,
                          borderColor: "rgba(22, 101, 52, 0.18)",
                          backgroundColor: themeColors.green.light,
                          cursor: "pointer",
                          minHeight: { xs: 560, sm: 600, md: 640 },
                          height: { xs: 560, sm: 600, md: 640 },
                          boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
                          transformOrigin: "center",

                          // Keep text identical on hover
                          "& .MuiTypography-root": {
                            textShadow: "none",
                          },
                          "&:hover": {
                            transform: { xs: "none", md: "scale(0.985)" },
                            boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
                            borderColor: "rgba(22, 101, 52, 0.18)",
                          },
                          "&:hover .MuiTypography-root": {
                            textShadow: "none",
                          },
                        }}
                      >
                        <Box sx={{ position: "relative" }}>
                          <CardMedia
                            component="img"
                            image={
                              getFullImageUrl(
                                typeof event?.image === "string"
                                  ? event.image
                                  : event?.image?.url || event?.image?.secure_url || ""
                              ) || "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=500"
                            }
                            alt={event.title}
                            sx={{
                              height: { xs: 230, sm: 260 },
                              width: "100%",
                              display: "block",
                              objectFit: "cover",
                            }}
                          />
                          <Box
                            sx={{
                              position: "absolute",
                              top: { xs: 10, sm: 12 },
                              left: { xs: 10, sm: 12 },
                              backgroundColor: tagBg,
                              color: themeColors.green.dark,
                              fontWeight: 900,
                              fontSize: { xs: "0.80rem", sm: "0.8rem" },
                              px: { xs: 2, sm: 3 },
                              py: { xs: 0.45, sm: 0.6 },
                              borderRadius: "14px",
                            }}
                          >
                            {tag}
                          </Box>
                        </Box>

                        <CardContent
                          sx={{
                            p: { xs: 4, sm: 3 },
                            "&:last-child": { pb: { xs: 2, sm: 3 } },
                            display: "flex",
                            flexDirection: "column",
                            flex: 1,
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              mb: 1.2,
                              textAlign: "center",
                              color: themeColors.black,
                              fontWeight: 900,
                              fontSize: { xs: "1.35rem", sm: "1.5rem", md: "1.65rem" },
                              lineHeight: 1.2,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {event.title || "Event"}
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              mb: 2.2,
                              textAlign: "center",
                              color: themeColors.black,
                              fontWeight: 700,
                              lineHeight: 1.4,
                              fontSize: { xs: "0.85rem", sm: "0.95rem" },
                              display: "-webkit-box",
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {getEventSummaryText(event)}
                          </Typography>

                          <Box sx={{ 
                            display: "flex", 
                            alignItems: "center", 
                            mb: 1.5, 
                            gap: 1 
                          }}>
                            <CalendarTodayIcon sx={{ 
                              fontSize: { xs: 16, sm: 18 }, 
                              color: themeColors.black 
                            }} />
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: themeColors.black, 
                                fontWeight: 700,
                                fontSize: { xs: "0.85rem", sm: "0.95rem" },
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {formatDateDDMMYYYY(event.date) || "11-03-2026"}
                            </Typography>
                          </Box>

                          <Box sx={{ 
                            display: "flex", 
                            alignItems: "flex-start", 
                            mb: 2, 
                            gap: 1 
                          }}>
                            <LocationOnIcon sx={{ 
                              fontSize: { xs: 16, sm: 18 }, 
                              color: themeColors.black, 
                              mt: "2px" 
                            }} />
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: themeColors.black, 
                                fontWeight: 700, 
                                lineHeight: 1.25,
                                fontSize: { xs: "0.85rem", sm: "0.95rem" },
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {locationShort}
                            </Typography>
                          </Box>

                          <Box sx={{ mt: "auto" }}>
                            <Divider sx={{ 
                              my: { xs: 1.5, sm: 2 }, 
                              borderColor: themeColors.border 
                            }} />
                            
                            <Typography
                              variant="body2"
                              sx={{
                                textAlign: "center",
                                color: themeColors.black,
                                fontWeight: 900,
                                fontSize: { xs: "1.2rem", sm: "1.25rem" },
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              Organised By:- {event.club || "LUCSSC"}
                            </Typography>
                          </Box>
                        </CardContent>
                      </EventCardNew>
                    );
                  })}
                </Box>
                
                {/* Mobile event count */}
                {isSmallMobile && visibleEvents.length > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      textAlign: "center",
                      mt: 2,
                      color: themeColors.text.muted,
                      fontWeight: 500,
                    }}
                  >
                    {visibleEvents.length} events available
                  </Typography>
                )}
              </Box>
            )}

            {/* No Events Found */}
            {!loading && visibleEvents.length === 0 && (
              <Box sx={{ 
                textAlign: "center", 
                py: { xs: 6, sm: 8, md: 10 },
                px: { xs: 2, sm: 3 },
                background: "white",
                borderRadius: { xs: "16px", sm: "20px" },
                boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
                mt: { xs: 2, sm: 4 },
                border: "1px solid #F1F5F9",
                mx: { xs: -1, sm: 0 },
              }}>
                <EventIcon sx={{ 
                  fontSize: { xs: 60, sm: 80 }, 
                  color: "#CBD5E1", 
                  mb: { xs: 2, sm: 3 } 
                }} />
                <Typography 
                  variant="h5" 
                  sx={{ 
                    mb: 2, 
                    color: themeColors.text.primary, 
                    fontWeight: 700,
                    fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.5rem" },
                  }}
                >
                  No events found
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: themeColors.text.muted, 
                    mb: { xs: 3, sm: 4 },
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    px: { xs: 2, sm: 0 },
                  }}
                >
                  Try changing your search or filter criteria
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => {
                    setSearch("");
                    setFilter("all");
                  }}
                  sx={{
                    background: themeColors.buttonGradient,
                    borderRadius: "50px",
                    px: { xs: 3, sm: 4 },
                    py: { xs: 1, sm: 1.5 },
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: { xs: "0.875rem", sm: "0.95rem" },
                    width: { xs: "100%", sm: "auto" },
                    maxWidth: { xs: "300px", sm: "none" },
                    mx: { xs: "auto", sm: 0 },
                    "&:hover": {
                      background: themeColors.buttonHover,
                    },
                  }}
                >
                  Clear Filters
                </Button>
              </Box>
            )}
          </Container>
        </Box>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ 
          vertical: isSmallMobile ? "top" : "bottom", 
          horizontal: "center" 
        }}
        sx={{
          bottom: { xs: "auto", sm: 16 },
          top: { xs: 16, sm: "auto" },
        }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ 
            width: "100%",
            fontSize: { xs: "0.875rem", sm: "1rem" },
            py: { xs: 0.5, sm: 1 },
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <div ref={footerRef}>
        {!embedded && <PostLoginFooter />}
      </div>
    </Box>
  );
}